import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion, useMotionValue, useSpring } from "framer-motion";
import { useCountUp } from "@/hooks/useCountUp";

const EASE = [0.22, 1, 0.36, 1];

// Sceptical observations per engine state — one at a time, enter -> read -> exit.
const THOUGHTS_BY_STATE = {
  idle: ["Let's see what you've actually got.", "Show me the idea."],
  thinking: ["Who actually buys it?", "Where's the real demand?", "Can you reach the customer?"],
  sceptical: ["That's an assumption.", "No evidence yet. Let's test that.", "Nice idea. Prove it."],
  verdict: ["Real demand. Worth testing.", "The numbers hold up.", "Now prove it for real."],
};

// Nodes clustered to the LEFT of / around the donkey. g = engine phase that wakes it.
const NODES = [
  { label: "SKILLS", x: 44, y: 24, g: 0 },
  { label: "BUDGET", x: 39, y: 42, g: 0 },
  { label: "TIME", x: 43, y: 60, g: 0 },
  { label: "DEMAND?", x: 50, y: 16, g: 1 },
  { label: "COMPETITION?", x: 37, y: 32, g: 1 },
  { label: "RISK?", x: 39, y: 52, g: 2 },
  { label: "EVIDENCE?", x: 46, y: 70, g: 2 },
];
const FOCAL = { x: 66, y: 20 };

// Evidence per phase — research gathers, challenge REJECTS a weak assumption, verdict confirms.
const EVIDENCE = {
  1: [{ t: "SEARCH DEMAND", s: "ok" }, { t: "COMPETITION HIGH", s: "warn" }],
  2: [{ t: "DEMAND ASSUMED", s: "warn" }, { t: "NO EVIDENCE YET", s: "bad" }],
  3: [{ t: "CUSTOMER PAIN VERIFIED", s: "ok" }, { t: "STARTUP COST ££", s: "warn" }],
};
const COUNTERS = [
  { n: 7, l: "EVIDENCE POINTS" },
  { n: 3, l: "ASSUMPTIONS CHALLENGED" },
  { n: 2, l: "IDEAS ELIMINATED" },
  { n: 1, l: "WORTH PROVING" },
];

// Four real rendered poses (background removed from the reference) — crossfaded by engine state.
const MASCOT = {
  idle: "/mascot/idle.png",
  thinking: "/mascot/thinking.png",
  sceptical: "/mascot/sceptical.png",
  verdict: "/mascot/verdict.png",
};
const RIM = {
  idle: "rgba(249,115,22,0.55)",
  thinking: "rgba(245,166,35,0.6)",
  sceptical: "rgba(229,80,70,0.55)",
  verdict: "rgba(255,215,120,0.75)",
};

function useEnginePhase(reduce) {
  const [state, setState] = useState({ phase: 0, tick: 0 });
  useEffect(() => {
    if (reduce) return;
    const on = (e) => setState((p) => ({ phase: e.detail?.phase ?? 0, tick: p.tick + 1 }));
    window.addEventListener("donkey-engine-phase", on);
    return () => window.removeEventListener("donkey-engine-phase", on);
  }, [reduce]);
  return state;
}

function Counter({ n, l, active, reduce, delay }) {
  const v = useCountUp(n, active, { duration: 800, reduce });
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 6 }}
      transition={{ delay, duration: 0.4, ease: EASE }}
      className="flex items-baseline gap-1.5"
    >
      <span className="font-mono text-lg text-donkey-warmorange leading-none">{v}</span>
      <span className="text-[10px] uppercase tracking-wider text-donkey-grey">{l}</span>
    </motion.div>
  );
}

const STATUS = {
  ok: { c: "text-donkey-green border-donkey-green/40 bg-donkey-green/10", m: "✓" },
  warn: { c: "text-donkey-amber border-donkey-amber/40 bg-donkey-amber/10", m: "!" },
  bad: { c: "text-donkey-red border-donkey-red/40 bg-donkey-red/10 line-through", m: "✕" },
};

// DESKTOP: the Donkey Intelligence Environment. Four pose states; the SYSTEM around him tells the analysis story.
export function HeroDonkey() {
  const reduce = useReducedMotion();
  const { phase, tick } = useEnginePhase(reduce);
  const verdict = phase === 3;
  const state = verdict ? "verdict" : phase === 0 ? "idle" : phase === 1 ? "thinking" : "sceptical";
  const pool = THOUGHTS_BY_STATE[state];
  const thought = pool[tick % pool.length];
  const cards = EVIDENCE[phase] || [];

  // Very subtle mouse-reactive light field (never moves the donkey).
  const stageRef = useRef(null);
  const mx = useMotionValue(70);
  const my = useMotionValue(40);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });
  useEffect(() => {
    if (reduce) return;
    const onMove = (e) => {
      const el = stageRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (e.clientX < r.left || e.clientY < r.top || e.clientY > r.bottom) return;
      mx.set(((e.clientX - r.left) / r.width) * 100);
      my.set(((e.clientY - r.top) / r.height) * 100);
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [reduce, mx, my]);

  return (
    <div ref={stageRef} className="hidden lg:block absolute inset-y-0 right-0 w-[52%] z-0 pointer-events-none overflow-hidden" aria-hidden>
      {/* static depth + floor + rim base (no idle motion) */}
      <div className="absolute right-[4%] top-1/2 -translate-y-1/2 w-[42rem] h-[42rem] rounded-full bg-donkey-orange/12 blur-[130px]" />
      <div className="absolute bottom-[16%] right-[9%] w-[32rem] h-[11rem] rounded-[50%] bg-donkey-orange/25 blur-[70px]" />

      {/* subtle mouse-reactive glow in the intelligence field */}
      {!reduce && (
        <motion.div
          className="absolute w-[16rem] h-[16rem] rounded-full bg-donkey-orange/10 blur-[80px]"
          style={{ left: sx, top: sy, x: "-50%", y: "-50%" }}
        />
      )}

      {/* connector + data lines (SVG). Pulses travel engine -> donkey; converge on verdict. */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        {NODES.map((n, i) => {
          const on = n.g === phase || verdict;
          return (
            <motion.line
              key={i}
              x1={n.x} y1={n.y} x2={FOCAL.x} y2={FOCAL.y}
              stroke="#F5A623" strokeWidth={verdict ? 0.18 : 0.12}
              initial={false}
              animate={{ strokeOpacity: on ? (verdict ? 0.7 : 0.5) : 0.08 }}
              transition={{ duration: 0.5 }}
            />
          );
        })}
        <line x1="0" y1="46" x2={FOCAL.x} y2={FOCAL.y} stroke="#F97316" strokeWidth="0.1" strokeOpacity="0.18" />
        {!reduce && (
          <motion.circle
            key={`feed-${tick}`}
            r="0.7" fill="#FFE9B0"
            initial={{ cx: 0, cy: 46, opacity: 0 }}
            animate={{ cx: FOCAL.x, cy: FOCAL.y, opacity: [0, 1, 1, 0] }}
            transition={{ duration: 0.9, ease: "easeIn" }}
            style={{ filter: "drop-shadow(0 0 2px #FFE9B0)" }}
          />
        )}
        {/* verdict: nodes converge toward the donkey */}
        {!reduce && verdict && NODES.filter((n) => n.g <= 2).slice(0, 4).map((n, i) => (
          <motion.circle
            key={`conv-${i}`}
            r="0.6" fill="#FFD27A"
            initial={{ cx: n.x, cy: n.y, opacity: 0 }}
            animate={{ cx: FOCAL.x, cy: FOCAL.y, opacity: [0, 1, 0] }}
            transition={{ duration: 0.8, delay: 0.1 + i * 0.1, ease: "easeIn" }}
            style={{ filter: "drop-shadow(0 0 2px #FFD27A)" }}
          />
        ))}
      </svg>

      {/* intelligence nodes — wake on their phase (lock bright on verdict), then settle */}
      {NODES.map((n, i) => {
        const on = n.g === phase;
        return (
          <motion.div
            key={i}
            className="absolute"
            style={{ left: `${n.x}%`, top: `${n.y}%`, transform: "translate(-50%,-50%)" }}
            initial={false}
            animate={{ opacity: verdict ? 0.85 : on ? 1 : 0.28, scale: on || verdict ? 1 : 0.92 }}
            transition={{ duration: 0.45, ease: EASE }}
          >
            <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-wider backdrop-blur-sm
              ${on || verdict ? "border-donkey-orange/60 bg-donkey-orange/15 text-donkey-warmorange" : "border-white/10 bg-donkey-bg/50 text-donkey-grey"}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${on || verdict ? "bg-donkey-orange" : "bg-white/20"}`} />
              {n.label}
            </span>
          </motion.div>
        );
      })}

      {/* evidence cards — arrive, settle, exit. Rejected ones cross out (personality: the Donkey pushes back). */}
      <div className="absolute left-[40%] top-[46%] w-[15rem] space-y-2">
        <AnimatePresence mode="popLayout">
          {!reduce && cards.map((c, i) => (
            <motion.div
              key={`${phase}-${c.t}`}
              initial={{ opacity: 0, x: -24, filter: "blur(4px)" }}
              animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, x: 18, filter: "blur(3px)" }}
              transition={{ duration: 0.5, delay: 0.25 + i * 0.3, ease: EASE }}
              className={`flex items-center justify-between rounded-lg border px-3 py-1.5 text-[11px] font-medium backdrop-blur-sm ${STATUS[c.s].c}`}
            >
              <span>{c.t}</span>
              <span className="ml-2 font-mono">{STATUS[c.s].m}</span>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* sceptical thought — one at a time, tied to the engine state */}
      <div className="absolute left-[3%] top-[9%] w-[14rem]">
        <AnimatePresence mode="wait">
          {!reduce && (
            <motion.div
              key={tick}
              initial={{ opacity: 0, y: 10, filter: "blur(4px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -8, filter: "blur(3px)" }}
              transition={{ duration: 0.45, ease: EASE }}
              className={`relative rounded-2xl rounded-bl-sm border bg-donkey-bg/80 backdrop-blur-sm px-3.5 py-2 text-sm italic
                ${state === "sceptical" ? "border-donkey-red/40 text-donkey-warm" : "border-donkey-orange/30 text-donkey-warm/90"}`}
            >
              “{thought}”
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* analytical counters — assemble on verdict, then dissolve */}
      <div className="absolute left-[40%] bottom-[14%]">
        <AnimatePresence>
          {verdict && !reduce && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="space-y-1.5 rounded-xl border border-white/10 bg-donkey-bg/70 backdrop-blur-sm px-4 py-3"
            >
              {COUNTERS.map((c, i) => (
                <Counter key={c.l} n={c.n} l={c.l} active delay={0.1 + i * 0.12} reduce={reduce} />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ---------- THE DONKEY: real pose STATE-SWAP, cinematic transitions, never distorted ---------- */}
      <div className="absolute bottom-[20%] right-[5%] xl:right-[8%] h-[58%] max-h-[520px] aspect-[101/187]" data-testid="hero-donkey">
        {/* state rim-light: bursts and settles when the pose changes */}
        {!reduce && (
          <motion.div
            key={`rim-${state}`}
            className="absolute inset-x-[6%] bottom-[2%] top-[8%] rounded-full blur-[42px]"
            style={{ background: `radial-gradient(circle at 60% 55%, ${RIM[state]}, transparent 68%)` }}
            initial={{ opacity: 0.18 }}
            animate={{ opacity: [0.18, 0.7, 0.34] }}
            transition={{ duration: 1.1, ease: "easeOut" }}
          />
        )}

        {/* the pose images crossfade; the incoming one settles with a tiny depth shift (no wobble/scale pulsing) */}
        <AnimatePresence initial={false}>
          <motion.img
            key={state}
            src={MASCOT[state]}
            alt=""
            decoding="async"
            className="absolute inset-0 h-full w-full object-contain object-bottom drop-shadow-[0_18px_45px_rgba(0,0,0,0.55)]"
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? undefined : { opacity: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
          />
        </AnimatePresence>

        {/* foreground light-pass across the character on each state change (single sweep, then gone) */}
        {!reduce && (
          <motion.div
            key={`sweep-${tick}`}
            className="absolute inset-0 skew-x-[-14deg]"
            style={{ background: "linear-gradient(100deg, transparent, rgba(255,233,176,0.22), transparent)" }}
            initial={{ x: "-70%", opacity: 0 }}
            animate={{ x: ["-70%", "170%"], opacity: [0, 0.9, 0] }}
            transition={{ duration: 0.9, ease: "easeInOut" }}
          />
        )}

        {/* VERDICT: blueprint catches a moving gold light (overlay only — the image is untouched) */}
        {!reduce && (
          <>
            <motion.div
              className="absolute left-[18%] bottom-[8%] w-[62%] h-[26%] rounded-[40%] blur-[22px]"
              style={{ background: "radial-gradient(circle, rgba(255,233,176,0.9), rgba(245,166,35,0.3) 55%, transparent 72%)" }}
              initial={{ opacity: 0 }}
              animate={verdict ? { opacity: [0, 0.95, 0.45] } : { opacity: 0 }}
              transition={{ duration: 1.3, ease: "easeOut", delay: 0.45 }}
            />
            <motion.div
              className="absolute left-[16%] bottom-[6%] w-[30%] h-[30%] skew-x-[-16deg]"
              style={{ background: "linear-gradient(100deg, transparent, rgba(255,233,176,0.6), transparent)" }}
              initial={{ x: "-60%", opacity: 0 }}
              animate={verdict ? { x: ["-60%", "220%"], opacity: [0, 0.9, 0] } : { opacity: 0 }}
              transition={{ duration: 1.1, ease: "easeInOut", delay: 0.55 }}
            />
          </>
        )}
      </div>

      {/* floor light responds to the verdict */}
      {!reduce && (
        <motion.div
          className="absolute bottom-[16%] right-[9%] w-[34rem] h-[13rem] rounded-[50%] bg-donkey-orange/40 blur-[70px]"
          initial={{ opacity: 0 }}
          animate={verdict ? { opacity: [0, 0.9, 0.3] } : { opacity: 0 }}
          transition={{ duration: 1.4, ease: "easeOut", delay: 0.45 }}
        />
      )}

      {/* occasional foreground particles rising on a state change, then gone */}
      {!reduce && [0, 1, 2].map((i) => (
        <motion.div
          key={`p-${tick}-${i}`}
          className="absolute w-1 h-1 rounded-full bg-donkey-warmorange"
          style={{ left: `${60 + i * 9}%`, bottom: "30%" }}
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: [0, 0.8, 0], y: -90 - i * 20 }}
          transition={{ duration: 1.6, delay: 0.2 + i * 0.18, ease: "easeOut" }}
        />
      ))}
    </div>
  );
}

// MOBILE: unchanged simplified stage (kept as-is for this desktop-only pass).
export function DonkeyStageMobile() {
  const reduce = useReducedMotion();
  const { phase, tick } = useEnginePhase(reduce);
  const state = phase === 3 ? "verdict" : phase === 0 ? "idle" : phase === 1 ? "thinking" : "sceptical";
  const pool = THOUGHTS_BY_STATE[state];
  const thought = pool[tick % pool.length];
  const labels = ["Understanding you", "Researching demand", "Challenging assumptions", "Verdict ready"];
  return (
    <div className="lg:hidden mb-6" data-testid="donkey-stage-mobile">
      <div className="relative rounded-2xl border border-donkey-orange/25 bg-donkey-bg/60 overflow-hidden p-4">
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-donkey-orange/15 blur-[60px]" />
        <div className="relative flex items-center gap-3">
          <img
            src="/donkey-hero.png" alt="" decoding="async"
            className="h-24 w-auto object-contain object-bottom shrink-0 drop-shadow-[0_0_30px_rgba(249,115,22,0.3)]"
          />
          <div className="min-w-0 flex-1">
            <AnimatePresence mode="wait">
              <motion.p
                key={tick}
                initial={reduce ? false : { opacity: 0, y: 8, filter: "blur(3px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                exit={reduce ? undefined : { opacity: 0, y: -6, filter: "blur(2px)" }}
                transition={{ duration: 0.4, ease: EASE }}
                className="text-sm italic text-donkey-warm/90 leading-snug break-words"
              >
                “{thought}”
              </motion.p>
            </AnimatePresence>
            <div className="mt-2 flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full ${phase === 3 ? "bg-donkey-green" : "bg-donkey-orange"} animate-pulse-soft`} />
              <span className="text-[11px] uppercase tracking-wider text-donkey-grey">{labels[phase] || labels[0]}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HeroDonkey;
